import type {
  UnifiedProduct,
  UnifiedProductAttributeValue,
  UnifiedProductTypedAttributeValue,
} from '@noths/polaris-client-unified-product-service';

import type { ProductAttributes } from 'src/redux/product/types';

const toCamelCase = (key: string): string =>
  key.toLowerCase().replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());

/**
 * Derive boolean flags from the presence of a custom attribute.
 */
const PRESENCE_DERIVED_ATTRIBUTES: Record<string, string> = {
  exclusive: 'exclusive',
  madeInBritain: 'pimMadeInBritain',
  returnable: 'returnable',
  handmade: 'handmade',
};

const EXCLUDED_ATTRIBUTE_KEYS = new Set([
  'complianceCategory',
  'lowCommitmentProductFl',
  'lowCommitmentProductType',
]);

const getCustomAttributeValue = (
  value: UnifiedProductTypedAttributeValue,
): UnifiedProductAttributeValue => {
  if (!value) {
    return null;
  }

  if ('numValue' in value) {
    return value.numValue;
  }

  if ('boolValue' in value) {
    return value.boolValue;
  }

  if ('values' in value) {
    return value.values;
  }

  return value.value;
};

/**
 * Takes the whole product rather than its `attributes`, because `customAttributes` sits alongside
 * `attributes` on the UPS product rather than inside it: the nested copy is deprecated in the UPS
 * schema in favour of the top-level one the shared client selects.
 */
export const adaptUnifiedProductAttributesToStore = ({
  attributes: { highlyRated, starBrand },
  customAttributes,
}: UnifiedProduct): ProductAttributes => {
  const result = customAttributes.reduce<ProductAttributes>((acc, attribute) => {
    const key = toCamelCase(attribute.key);

    if (EXCLUDED_ATTRIBUTE_KEYS.has(key)) {
      return acc;
    }

    acc[key] = getCustomAttributeValue(attribute.value);
    return acc;
  }, {});

  for (const [derivedKey, sourceKey] of Object.entries(PRESENCE_DERIVED_ATTRIBUTES)) {
    result[derivedKey] = sourceKey in result;
  }

  if (highlyRated !== undefined) {
    result.highlyRated = highlyRated;
  }
  if (starBrand !== undefined) {
    result.starBrand = starBrand;
  }

  return result;
};
